import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './apiClient';
import { isDevelopment } from '../config/env';

/**
 * Configure the React Query client with optimized caching strategies
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: how long data is considered fresh
      // Sessions data: 2 minutes (frequently updated)
      // Dashboard stats: 1 minute (real-time feel)
      // User profile: 10 minutes (rarely changes)
      staleTime: 2 * 60 * 1000,
      
      // Cache time: how long unused data remains in cache
      gcTime: 15 * 60 * 1000, // 15 minutes
      
      // Retry configuration with smart logic
      retry: (failureCount, error) => {
        // Don't retry on client errors (4xx)
        if (error instanceof ApiError) {
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for server errors (5xx) and network errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff and jitter
      retryDelay: (attemptIndex) => {
        const baseDelay = Math.min(1000 * 2 ** attemptIndex, 30000);
        // Add jitter to prevent thundering herd
        const jitter = Math.random() * 0.1 * baseDelay;
        return baseDelay + jitter;
      },
      
      // Performance optimizations
      refetchOnWindowFocus: isDevelopment,
      refetchOnReconnect: true,
      refetchOnMount: true,
      
      // Network mode for better offline handling
      networkMode: 'online',
      
      // Show error notifications for failed queries
      useErrorBoundary: false,
      
      // Enable background refetching for better UX
      refetchInterval: false, // Disable automatic polling by default
      refetchIntervalInBackground: false,
    },
    mutations: {
      // Retry failed mutations with exponential backoff
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // Don't retry client errors
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        return failureCount < 2;
      },
      
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      
      // Network mode for mutations
      networkMode: 'online',
      
      // Show error notifications for failed mutations
      useErrorBoundary: false,
    },
  },
});

// Query key factory for consistent cache management
export const queryKeys = {
  sessions: {
    all: ['sessions'] as const,
    lists: () => [...queryKeys.sessions.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.sessions.lists(), filters] as const,
    details: () => [...queryKeys.sessions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.sessions.details(), id] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    chart: (timeRange?: any) => [...queryKeys.dashboard.all, 'chart', timeRange] as const,
    recent: () => [...queryKeys.dashboard.all, 'recent'] as const,
  },
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    preferences: () => [...queryKeys.user.all, 'preferences'] as const,
  },
} as const;

// Prefetching utilities for better performance
export const prefetchQueries = {
  sessions: {
    list: (filters?: any) => 
      queryClient.prefetchQuery({
        queryKey: queryKeys.sessions.list(filters),
        staleTime: 2 * 60 * 1000,
      }),
    detail: (id: string) =>
      queryClient.prefetchQuery({
        queryKey: queryKeys.sessions.detail(id),
        staleTime: 5 * 60 * 1000, // Session details change less frequently
      }),
  },
  dashboard: {
    stats: () =>
      queryClient.prefetchQuery({
        queryKey: queryKeys.dashboard.stats(),
        staleTime: 1 * 60 * 1000, // Fresh stats for dashboard
      }),
  },
};